import { useMemo } from "react"

import { useParams } from "react-router-dom"

import { GradeBadge } from "@l2r-front/l2r-common"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { Typography } from "@l2r-front/l2r-ui"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { useRiskZonesContext } from "../../contexts/RiskZonesContext"
import { useRiskZoneCluster } from "../../hooks/queries/useRiskZoneClusters"
import { useRiskZoneGrades } from "../../hooks/queries/useRiskZoneGrades"
import { useRiskZonesSurveys } from "../../hooks/queries/useRiskZonesSurveys"
import { riskZonesLegend } from "../../utils/getRiskZonesConfig"
import { useFormatApiDateString } from "../../utils/useFormatApiString"

import * as Styled from "./RiskZonesGrades.styled"

export function RiskZonesGrades(props) {

    const { className } = props

    const { riskZoneId, road } = useParams()

    const {
        data: surveys,
        isLoading: isSurveysLoading,
        isError: isSurveysError,
    } = useRiskZonesSurveys()

    const {
        data: riskZone,
        isLoading: isRiskZoneLoading,
        isError: isRiskZoneError,
    } = useRiskZoneCluster(riskZoneId, {}, { enabled: !!riskZoneId })

    const {
        data: grades,
        isLoading: isGradesLoading,
        isError: isGradesError,
    } = useRiskZoneGrades({
        group_by: "year",
        ...(road && { linear_location_road: road }),
    }, { enabled: !riskZoneId })

    const isLoading = isGradesLoading || isSurveysLoading || isRiskZoneLoading
    const isError = isGradesError || isSurveysError || (isRiskZoneError && !!riskZoneId)

    const { t } = useTranslation([I18N_NAMESPACE])
    const [state, dispatch] = useRiskZonesContext()
    const { currentYear } = state
    const { setCurrentYear } = dispatch
    const format = useFormatApiDateString()

    const yearlyStatusesOptions = useMemo(() => {
        if (isLoading || isError || !surveys?.length) {
            return []
        }
        const sortedSurveys = surveys.sort((a, b) => a.year - b.year)
        const firstYear = sortedSurveys[0].year
        const lastYear = sortedSurveys.at(-1).year
        const displayedYears = Array.from({ length: lastYear - firstYear + 1 }, (_, i) => {
            return firstYear + i
        })
        return (displayedYears || []).map(year => {
            const filteredDataItem = riskZoneId ? riskZone?.find(riskZone => riskZone.year === year)?.grade
                : grades?.find(item => item.year === year)?.average_grade
            return ({
                date: year,
                badgeComponent: <GradeBadge
                    grade={parseFloat(filteredDataItem) ?? null}
                    id={`riskZones-${year}`}
                    legend={riskZonesLegend}
                />,
                value: { uuid: year },
                uuid: year,
            })
        })
    }, [grades, isError, isLoading, riskZone, riskZoneId, surveys])

    const infoMessage = useMemo(() => {
        if (isLoading || isError || !surveys?.length) {
            return null
        }

        const currentSurvey = surveys?.find(item => item.year === parseInt(currentYear))
        if (!currentSurvey) {
            return t(I18N_NAMESPACE, "containers.riskZonesGrades.moduleNotAcquired", { year: currentYear })
        }
        const currentYearGrade = riskZoneId ? riskZone?.find(riskZone => riskZone.year === currentYear)?.grade
            : grades?.find(item => item.year === currentYear)?.average_grade
        const formattedDateFrom = format(currentSurvey?.date_from)
        const formattedDateTo = format(currentSurvey?.date_to)

        if (!currentYearGrade && riskZoneId) {
            return t(I18N_NAMESPACE, "containers.riskZonesGrades.noRiskZone", { year: currentYear, riskZoneId: riskZoneId })
        } else if (!currentYearGrade && road) {
            return t(I18N_NAMESPACE, "containers.riskZonesGrades.noRoadGrade", { year: currentYear })
        } else if (!currentYearGrade && !road) {
            return t(I18N_NAMESPACE, "containers.riskZonesGrades.noGrades", { year: currentYear })
        } else {
            return t(I18N_NAMESPACE, "containers.riskZonesGrades.infos", { dateFrom: formattedDateFrom, dateTo: formattedDateTo })
        }
    }, [
        isLoading,
        isError,
        surveys,
        grades,
        currentYear,
        riskZone,
        riskZoneId,
        road,
        format,
        t,
    ])

    return <Styled.Container>
        <Styled.YearSelector
            className={className}
            onClick={setCurrentYear}
            yearlyStatuses={yearlyStatusesOptions}
            selectedValue={{ uuid: currentYear }}
        />
        <Typography variant="Desktop & Mobile/Body/Small">
            {infoMessage}
        </Typography>
    </Styled.Container>
}